import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, orderBy, query, doc, updateDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { ShieldAlert, CheckCircle2, Ban, PauseCircle, RefreshCw, X, Calendar, ExternalLink } from 'lucide-react';

const CHANNEL_META = {
  web:       { label: 'Web',       color: 'bg-blue-100 text-blue-700' },
  referral:  { label: 'Referans',  color: 'bg-green-100 text-green-700' },
  instagram: { label: 'Instagram', color: 'bg-purple-100 text-purple-700' },
  outreach:  { label: 'Outreach',  color: 'bg-orange-100 text-orange-700' },
  direct:    { label: 'Direkt',    color: 'bg-gray-100 text-gray-600' },
  other:     { label: 'Diğer',     color: 'bg-gray-100 text-gray-600' },
};

const PLAN_META_COLORS = {
  starter: 'bg-gray-100 text-gray-600',
  pro:     'bg-blue-100 text-blue-700',
  premium: 'bg-purple-100 text-purple-700',
};

const STATUS_META_COLORS = {
  trialing:  'bg-blue-100 text-blue-700',
  active:    'bg-green-100 text-green-700',
  past_due:  'bg-amber-100 text-amber-700',
  suspended: 'bg-orange-100 text-orange-700',
  canceled:  'bg-red-100 text-red-700',
};

const fmtDate = (d, lang) => {
  if (!d) return '—';
  const ts = d?.toDate ? d.toDate() : new Date(d);
  return ts.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const today30 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().split('T')[0];
};

const AdminPanel = () => {
  const { t, i18n } = useTranslation('settings');
  const { user, refreshToken } = useAuth();
  const [tokenReady, setTokenReady] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [activateModal, setActivateModal] = useState(null);
  const [periodEndInput, setPeriodEndInput] = useState('');
  const [activatePlanId, setActivatePlanId] = useState('pro');
  const [activateBillingCycle, setActivateBillingCycle] = useState('monthly');
  const [channelLoading, setChannelLoading] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [campaignLoading, setCampaignLoading] = useState({});

  useEffect(() => {
    refreshToken().finally(() => setTokenReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBusinesses = useCallback(async () => {
    try {
      setLoading(true);
      const snap = await getDocs(query(collection(db, 'businesses'), orderBy('createdAt', 'desc')));
      setBusinesses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      toast.error(t('admin.toastLoadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  useEffect(() => {
    getDocs(collection(db, 'campaigns'))
      .then((snap) => setCampaigns(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, []);

  const callManage = async (action, businessId, extra = {}) => {
    setActionLoading((p) => ({ ...p, [businessId]: action }));
    try {
      const fn = httpsCallable(functions, 'manageSubscription');
      await fn({ action, businessId, ...extra });
      toast.success(
        action === 'activate' ? t('admin.toastActivate') :
        action === 'suspend'  ? t('admin.toastSuspend') :
                                t('admin.toastCancel')
      );
      fetchBusinesses();
    } catch (err) {
      toast.error(err.message || t('admin.toastError'));
    } finally {
      setActionLoading((p) => { const n = { ...p }; delete n[businessId]; return n; });
    }
  };

  const updateCampaignId = async (bizId, campaignId) => {
    setCampaignLoading((p) => ({ ...p, [bizId]: true }));
    try {
      await updateDoc(doc(db, 'businesses', bizId), { campaignId: campaignId || null });
      setBusinesses((prev) => prev.map((b) => b.id === bizId ? { ...b, campaignId: campaignId || null } : b));
      toast.success('Kampanya güncellendi');
    } catch {
      toast.error('Güncelleme başarısız');
    } finally {
      setCampaignLoading((p) => { const n = { ...p }; delete n[bizId]; return n; });
    }
  };

  const updateChannel = async (bizId, channel) => {
    setChannelLoading(p => ({ ...p, [bizId]: true }));
    try {
      await updateDoc(doc(db, 'businesses', bizId), { salesChannel: channel });
      setBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, salesChannel: channel } : b));
      toast.success('Kanal güncellendi');
    } catch (e) {
      toast.error('Güncelleme başarısız');
    } finally {
      setChannelLoading(p => { const n = { ...p }; delete n[bizId]; return n; });
    }
  };

  const handleActivate = async () => {
    if (!periodEndInput) { toast.error(t('admin.periodEndRequired')); return; }
    const periodEndDate = new Date(periodEndInput).toISOString();
    await callManage('activate', activateModal.id, { periodEndDate, planId: activatePlanId, billingCycle: activateBillingCycle });
    setActivateModal(null);
  };

  const getStatusLabel = (status) => {
    const map = { trialing: t('admin.statusTrialing'), active: t('admin.statusActive'), past_due: t('admin.statusPastDue'), suspended: t('admin.statusSuspended'), canceled: t('admin.statusCanceled') };
    return map[status] || status;
  };
  const getPlanLabel = (planId) => {
    const map = { starter: t('admin.planStarter'), pro: t('admin.planPro'), premium: t('admin.planPremium') };
    return map[planId] || planId;
  };
  const StatusBadge = ({ status }) => {
    const color = STATUS_META_COLORS[status] || 'bg-gray-100 text-gray-600';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {getStatusLabel(status)}
      </span>
    );
  };

  const Spinner = () => (
    <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
  );

  if (!tokenReady) {
    return (
      <div className="flex justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!user?.superadmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-400">
        <ShieldAlert className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-sm font-medium">{t('admin.accessDenied')}</p>
        <p className="text-xs mt-1 text-gray-300">{t('admin.accessDeniedSub')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('admin.subtitle')}</p>
        </div>
        <button
          onClick={fetchBusinesses}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {t('admin.refreshBtn')}
        </button>
      </div>

      {/* Super-admin warning banner */}
      <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-xs text-amber-700">
        <ShieldAlert className="h-4 w-4 flex-shrink-0" />
        {t('admin.superadminBanner')}
      </div>

      {/* Stats Bar */}
      {!loading && businesses.length > 0 && (() => {
        const statusCounts = businesses.reduce((acc, b) => {
          const s = b.subscription?.status || 'unknown';
          acc[s] = (acc[s] || 0) + 1;
          return acc;
        }, {});
        const channelCounts = businesses.reduce((acc, b) => {
          const c = b.salesChannel || 'unknown';
          acc[c] = (acc[c] || 0) + 1;
          return acc;
        }, {});
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">Abonelik Durumu</div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { key: 'trialing',  label: t('admin.statusTrialing'), color: 'bg-blue-100 text-blue-700' },
                  { key: 'active',    label: t('admin.statusActive'),   color: 'bg-green-100 text-green-700' },
                  { key: 'past_due',  label: t('admin.statusPastDue'),  color: 'bg-amber-100 text-amber-700' },
                  { key: 'suspended', label: t('admin.statusSuspended'),color: 'bg-orange-100 text-orange-700' },
                  { key: 'canceled',  label: t('admin.statusCanceled'), color: 'bg-red-100 text-red-700' },
                ].map(({ key, label, color }) =>
                  statusCounts[key] ? (
                    <span key={key} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${color}`}>
                      {statusCounts[key]} {label}
                    </span>
                  ) : null
                )}
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">Satış Kanalı</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(CHANNEL_META).map(([key, { label, color }]) =>
                  channelCounts[key] ? (
                    <span key={key} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${color}`}>
                      {channelCounts[key]} {label}
                    </span>
                  ) : null
                )}
                {channelCounts['unknown'] ? (
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-400">
                    {channelCounts['unknown']} Belirsiz
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : businesses.length === 0 ? (
        <p className="text-center text-gray-400 py-12 text-sm">{t('admin.emptyState')}</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('admin.colBusiness')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('admin.colStatus')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('admin.colPlan')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('admin.colEnd')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">{t('admin.colProvider')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">Kanal</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wide">Kayıt Kaynağı</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {businesses.map((biz) => {
                  const sub = biz.subscription || {};
                  const isLoading = !!actionLoading[biz.id];
                  return (
                    <tr key={biz.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{biz.name || '—'}</div>
                        <div className="text-xs text-gray-400">{biz.email || biz.id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={sub.status} />
                        {sub.manuallyManaged && (
                          <span className="ml-1.5 text-xs text-gray-400">{t('admin.manualLabel')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const pmColor = PLAN_META_COLORS[sub.planId] || PLAN_META_COLORS.pro;
                          return (
                            <div>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${pmColor}`}>
                                {getPlanLabel(sub.planId)}
                              </span>
                              {sub.billingCycle && (
                                <span className="ml-1.5 text-xs text-gray-400">
                                  {sub.billingCycle === 'yearly' ? t('admin.billingYearly') : t('admin.billingMonthly')}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {sub.status === 'trialing'
                          ? fmtDate(sub.trialEndsAt, i18n.language)
                          : fmtDate(sub.currentPeriodEnd, i18n.language)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {sub.provider || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <select
                            value={biz.salesChannel || ''}
                            onChange={(e) => updateChannel(biz.id, e.target.value)}
                            disabled={!!channelLoading[biz.id]}
                            className="text-xs px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:opacity-50 max-w-[110px]"
                          >
                            <option value="">Belirsiz</option>
                            <option value="web">Web</option>
                            <option value="referral">Referans</option>
                            <option value="instagram">Instagram</option>
                            <option value="outreach">Outreach</option>
                            <option value="direct">Direkt</option>
                            <option value="other">Diğer</option>
                          </select>
                          {biz.salesChannel === 'outreach' && biz.leadId && (
                            <Link
                              to="/admin/leads"
                              className="p-1 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors"
                              title="Lead kaydına git"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                          )}
                          {biz.registrationSource && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-500 font-medium border border-blue-100 whitespace-nowrap"
                              title={biz.registrationSource.utmCampaign ? `Kampanya: ${biz.registrationSource.utmCampaign}` : 'Kayıt sırasında otomatik atandı'}
                            >
                              otomatik
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {biz.registrationSource ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs font-medium text-gray-700">
                              {biz.registrationSource.ref || biz.registrationSource.utmSource || 'direkt'}
                            </span>
                            {biz.registrationSource.utmCampaign && (
                              <span className="text-[10px] text-gray-400 truncate max-w-[120px]" title={biz.registrationSource.utmCampaign}>
                                {biz.registrationSource.utmCampaign}
                              </span>
                            )}
                            {biz.registrationSource.utmMedium && (
                              <span className="text-[10px] text-gray-400">{biz.registrationSource.utmMedium}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                        {campaigns.length > 0 && (
                          <select
                            value={biz.campaignId || ''}
                            onChange={(e) => updateCampaignId(biz.id, e.target.value)}
                            disabled={!!campaignLoading[biz.id]}
                            className="mt-1.5 text-[10px] px-1.5 py-1 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-full disabled:opacity-50 text-gray-600"
                          >
                            <option value="">Kampanya…</option>
                            {campaigns
                              .filter((c) => c.isActive !== false)
                              .map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                          </select>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* Activate */}
                          <button
                            onClick={() => { setPeriodEndInput(today30()); setActivatePlanId(biz.subscription?.planId || 'pro'); setActivateBillingCycle(biz.subscription?.billingCycle || 'monthly'); setActivateModal(biz); }}
                            disabled={isLoading || sub.status === 'active'}
                            title={t('admin.titleActivate')}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-30"
                          >
                            {isLoading && actionLoading[biz.id] === 'activate'
                              ? <Spinner />
                              : <CheckCircle2 className="h-4 w-4" />}
                          </button>

                          {/* Suspend */}
                          <button
                            onClick={() => callManage('suspend', biz.id)}
                            disabled={isLoading || sub.status === 'suspended'}
                            title={t('admin.titleSuspend')}
                            className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-30"
                          >
                            {isLoading && actionLoading[biz.id] === 'suspend'
                              ? <Spinner />
                              : <PauseCircle className="h-4 w-4" />}
                          </button>

                          {/* Cancel */}
                          <button
                            onClick={() => {
                              if (window.confirm(t('admin.cancelConfirm', { name: biz.name }))) {
                                callManage('cancel', biz.id);
                              }
                            }}
                            disabled={isLoading || sub.status === 'canceled'}
                            title={t('admin.titleCancel')}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30"
                          >
                            {isLoading && actionLoading[biz.id] === 'cancel'
                              ? <Spinner />
                              : <Ban className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activate Modal */}
      {activateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">{t('admin.modalTitle')}</h2>
              <button onClick={() => setActivateModal(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-600">
                <span className="font-medium">{activateModal.name}</span> {t('admin.modalFor')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.planLabel')}</label>
                  <select
                    value={activatePlanId}
                    onChange={(e) => setActivatePlanId(e.target.value)}
                    className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                  >
                    <option value="starter">{t('admin.planStarter')}</option>
                    <option value="pro">{t('admin.planPro')}</option>
                    <option value="premium">{t('admin.planPremium')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.cycleLabel')}</label>
                  <select
                    value={activateBillingCycle}
                    onChange={(e) => setActivateBillingCycle(e.target.value)}
                    className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                  >
                    <option value="monthly">{t('admin.billingMonthly')}</option>
                    <option value="yearly">{t('admin.billingYearly')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.periodEndLabel')}</label>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <input
                    type="date"
                    value={periodEndInput}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setPeriodEndInput(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setActivateModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                {t('admin.cancelBtn')}
              </button>
              <button
                onClick={handleActivate}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                {t('admin.activateBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
